import { blockSubjectInputGroups, subjectInputGroupName, type SubjectWithOptionalFee } from 'venn-state';
import {
  blockSettings,
  subjectGroupRequestSubjects,
  blockRequestSubjects,
  blockSubjects,
  analysisSubjects,
} from 'venn-state';
import { useRecoilValue, waitForAll } from 'recoil';
import type { AnalysisSubject, CustomizableBlockSetting } from 'venn-utils';
import { getBlockSubjectTypeName, plural } from 'venn-utils';
import { compact, some } from 'lodash';
import { useMemo } from 'react';
import type { ValidationError } from '../types';

export const useBlockSubjectInputGroupsValidation = (blockId: string): ValidationError[] => {
  const inputGroupIds = useRecoilValue(blockSubjectInputGroups(blockId));
  const subjectGroups = useRecoilValue(
    waitForAll(
      Object.fromEntries(
        inputGroupIds.map((id) => [
          id,
          waitForAll({
            groupName: subjectInputGroupName(id),
            groupSubjects: subjectGroupRequestSubjects([blockId, id]),
          }),
        ]),
      ),
    ),
  );
  const allSubjects = useRecoilValue(blockRequestSubjects(blockId));
  const unfilteredRawSubjects = useRecoilValue(blockSubjects(blockId));
  const unfilteredSubjects = useRecoilValue(analysisSubjects(unfilteredRawSubjects));
  const blockSetting = useRecoilValue(blockSettings(blockId));

  return useMemo(() => {
    return compact(
      inputGroupIds.map((id) => {
        const { groupName, groupSubjects } = subjectGroups[id]!;
        return validateSubjects({ groupName, groupSubjects, allSubjects, blockSetting, unfilteredSubjects });
      }),
    );
  }, [inputGroupIds, subjectGroups, allSubjects, blockSetting, unfilteredSubjects]);
};

const validateSubjects = ({
  groupSubjects,
  groupName,
  allSubjects,
  blockSetting,
  unfilteredSubjects,
}: {
  groupSubjects: SubjectWithOptionalFee[];
  groupName: string;
  allSubjects: SubjectWithOptionalFee[];
  blockSetting: CustomizableBlockSetting;
  unfilteredSubjects: AnalysisSubject[];
}): ValidationError | undefined => {
  if (groupSubjects.length === 0) {
    if (some(unfilteredSubjects, (subject) => subject.isHistoricalPortfolio)) {
      return {
        message: `Subject group "${groupName}" contains historical portfolios unsupported by this block.`,
        type: 'ERROR',
      };
    }
    return {
      message: `Subject group "${groupName}" does not contain any ${getBlockSubjectTypeName(blockSetting)}s.`,
      type: 'ERROR',
    };
  }
  if (groupSubjects.length < blockSetting.requiredSubjects && allSubjects.length < blockSetting.requiredSubjects) {
    return {
      message: `Subject group "${groupName}" contains only ${groupSubjects.length} ${plural(groupSubjects.length, {
        1: getBlockSubjectTypeName(blockSetting),
        other: `${getBlockSubjectTypeName(blockSetting)}s`,
      })}.`,
      type: blockSetting.customBlockType === 'PORTFOLIO_COMPARISON' ? 'WARNING' : 'ERROR',
    };
  }
  return undefined;
};
